import { useEffect, useRef, useState } from 'react'
import { Icon, SparkCube } from '../../components/Brand'
import { chatSources, request } from '../api'
import { PageHeader } from './Common'

// Every starter below is deliberately dataset-agnostic: it names no customer,
// invoice or task id, so it keeps working after the workspace is reset and a
// different Priority workbook is uploaded. An earlier list hardcoded a customer
// id and silently degraded to "open balance is 0" once that customer was no
// longer in the book. All three were measured against the deployed runtime and
// return grounded sources inside the 30s gateway ceiling.
const suggestions = [
  'Which customers have the most overdue debt?',
  'Summarize the current collections workload.',
  'Which collection tasks should the team handle today?',
]

function answerText(answer) {
  if (typeof answer === 'string') return answer
  return answer?.message || answer?.text || answer?.answer || 'The agent returned an empty response.'
}

export default function CollectIt({ businessUnit = '' }) {
  const sessionId = useRef(`collections-${crypto.randomUUID?.() || Date.now()}`)
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const previousScope = useRef(businessUnit)

  useEffect(() => {
    if (previousScope.current === businessUnit) return
    const oldSession = sessionId.current
    previousScope.current = businessUnit
    sessionId.current = `collections-${crypto.randomUUID?.() || Date.now()}`
    setMessages([])
    setError(null)
    request('chat/reset', { method: 'POST', body: { session_id: oldSession } }).catch(() => {})
  }, [businessUnit])

  const send = async (value = draft, { appendUser = true } = {}) => {
    const message = value.trim()
    if (!message || sending) return
    if (appendUser) setMessages((current) => [...current, { role: 'user', text: message }])
    setDraft(''); setSending(true); setError(null)
    try {
      const data = await request('chat', { method: 'POST', body: { message, session_id: sessionId.current, business_unit: businessUnit || null } })
      setMessages((current) => [...current, { role: 'assistant', text: answerText(data.answer), sources: chatSources(data) }])
    } catch (caught) { setError(caught) }
    finally { setSending(false) }
  }

  const reset = async () => {
    setMessages([]); setError(null)
    try { await request('chat/reset', { method: 'POST', body: { session_id: sessionId.current } }) } catch { /* local reset remains useful offline */ }
  }

  return <div className="col-page col-chat-page">
    <PageHeader title="Collect-it" description="Ask grounded questions across customers, invoices, tasks, and escalations." meta={<span className="col-readonly"><Icon name="verified_user" />Read-only agent</span>} actions={messages.length > 0 && <button className="col-btn secondary" onClick={reset}><Icon name="refresh" />New conversation</button>} />
    <div className="col-chat-shell">
      <div className="col-chat-messages" aria-live="polite">
        {!messages.length && <div className="col-chat-welcome"><div className="col-chat-logo"><SparkCube /></div><h2>What would you like to know?</h2><p>I can investigate your live Collections data and explain what needs attention. Every answer is checked for grounding before it reaches you.</p><div className="col-suggestions">{suggestions.map((suggestion) => <button key={suggestion} onClick={() => send(suggestion)}><Icon name="north_east" />{suggestion}</button>)}</div></div>}
        {messages.map((message, index) => <div className={`col-message ${message.role}`} key={`${message.role}-${index}`}>
          {message.role === 'assistant' && <span className="col-message-avatar"><SparkCube /></span>}
          <div><span className="col-message-author">{message.role === 'assistant' ? 'Collect-it' : 'You'}</span><p>{message.text}</p>{message.sources?.length > 0 && <div className="col-source-row"><span>Grounded in</span>{message.sources.map((source, sourceIndex) => <b key={source.id || sourceIndex}>{source.label || source.title || source.id || String(source)}</b>)}</div>}</div>
        </div>)}
        {sending && <div className="col-message assistant"><span className="col-message-avatar"><SparkCube /></span><div><span className="col-message-author">Collect-it</span><div className="col-typing"><i /><i /><i /></div><small>Checking Collections records and grounding the answer…</small></div></div>}
        {error && <div className="col-chat-error"><Icon name="error" /><span><b>Collect-it couldn’t answer</b>{error.message}</span><button onClick={() => send(messages.at(-1)?.text || draft, { appendUser: false })}>Try again</button></div>}
      </div>
      <form className="col-composer" onSubmit={(event) => { event.preventDefault(); send() }}>
        <textarea rows="1" value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send() } }} placeholder="Ask about an account, invoice, task, or escalation…" aria-label="Message Collect-it" />
        <button type="submit" disabled={!draft.trim() || sending} aria-label="Send"><Icon name="arrow_upward" /></button>
        <small><Icon name="lock" /> Answers are read-only and grounded in the local Collections dataset.</small>
      </form>
    </div>
  </div>
}
